import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = ["Student", "Parent", "Educator", "Contractor or Trade Partner", "Community Member", "Other"];

const empty = { name: "", email: "", phone: "", role: "", message: "", keep_updated: false };

export default function Connect() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please select what best describes you.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="connect-page">
      <section className="bg-white pt-40 pb-24 md:pt-48 md:pb-32 blueprint-grid-light min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-head text-xs font-bold uppercase tracking-[0.25em] text-brand-dark mb-6" data-testid="connect-overline">Connect / Learn More</p>
              <h1 className="font-display uppercase text-navy text-5xl md:text-6xl leading-[0.95] mb-8" data-testid="connect-headline">
                Want to learn more about BuilderEDU?
              </h1>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed max-w-md" data-testid="connect-subtext">
                Whether you're a student, parent, educator, or trade professional — send us a note
                and we'll get back to you. Check the box to stay in the loop on BuilderEDU news.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-12 hidden lg:block overflow-hidden clip-frame">
                <img
                  src="/images/framing-work.jpg"
                  alt="Residential construction framing"
                  className="w-full h-72 object-cover"
                  data-testid="connect-image"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-navy/10 shadow-[0_24px_60px_-24px_rgba(14,21,46,0.25)] p-8 md:p-12 flex flex-col gap-7"
              data-testid="connect-form"
            >
              <div className="grid sm:grid-cols-2 gap-7">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">Name *</Label>
                  <Input id="name" required value={form.name} onChange={set("name")} placeholder="Your full name"
                    className="bg-slate-50 rounded-sm h-12 focus-visible:ring-brand" data-testid="connect-name-input" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">Email *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com"
                    className="bg-slate-50 rounded-sm h-12 focus-visible:ring-brand" data-testid="connect-email-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-7">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">
                    Phone <span className="text-slate-400 font-medium normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555"
                    className="bg-slate-50 rounded-sm h-12 focus-visible:ring-brand" data-testid="connect-phone-input" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">I am a: *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger className="bg-slate-50 rounded-sm h-12 focus:ring-brand" data-testid="connect-role-select">
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`connect-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="message" className="font-head text-xs font-bold uppercase tracking-[0.15em] text-navy">Message or Question *</Label>
                <Textarea id="message" required value={form.message} onChange={set("message")} rows={5}
                  placeholder="Tell us what you'd like to know about BuilderEDU..."
                  className="bg-slate-50 rounded-sm focus-visible:ring-brand" data-testid="connect-message-input" />
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="keep_updated"
                  checked={form.keep_updated}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, keep_updated: v === true }))}
                  className="rounded-sm data-[state=checked]:bg-brand data-[state=checked]:border-brand data-[state=checked]:text-navy-ink"
                  data-testid="connect-updates-checkbox"
                />
                <Label htmlFor="keep_updated" className="text-sm text-slate-600 cursor-pointer font-medium">
                  Keep me updated about BuilderEDU
                </Label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="connect-submit-button"
                className="group font-head font-bold uppercase tracking-[0.12em] text-sm bg-navy-ink text-white px-8 py-4 inline-flex items-center justify-center gap-3 hover:bg-brand hover:text-navy-ink hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 mt-2"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="group-hover:translate-x-1 transition-transform duration-300" />}
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
